import { useRef, useState } from 'react'
import { EInvoiceRepublishCandidates } from './einvoice-republish-candidates'
import type { ManualCandidate } from '@/lib/api/einvoice-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'

export function EInvoicePublishConfirm({
    token,
    onPublish,
    onComplete,
    orderCode,
    requireManualCheck = false,
    orderId,
    onUseCandidate,
}: {
    token: string
    onPublish: (token: string, notIssuedByHand?: boolean) => Promise<unknown>
    onComplete: (error?: string) => void
    orderCode?: string
    /** Sending an unanswered invoice again needs the person to say they checked
     *  MISA's own site, where staff may have issued it by hand. */
    requireManualCheck?: boolean
    /** With `requireManualCheck`, invoices made on MISA's website that may be
     *  this order's are listed so the person can see them before sending. */
    orderId?: string
    onUseCandidate?: (candidate: ManualCandidate) => void
}) {
    const [open, setOpen] = useState(false)
    const [checked, setChecked] = useState(false)
    const [checkError, setCheckError] = useState(false)
    const [pending, setPending] = useState(false)
    const inFlight = useRef(false)
    async function confirm() {
        if (inFlight.current) return
        if (requireManualCheck && !checked) {
            setCheckError(true)
            return
        }
        inFlight.current = true
        setPending(true)
        try {
            await (requireManualCheck
                ? onPublish(token, true)
                : onPublish(token))
            setOpen(false)
            onComplete()
        } catch (error) {
            setOpen(false)
            onComplete(
                error instanceof Error
                    ? error.message
                    : m.einvoice_publish_failed(),
            )
        } finally {
            inFlight.current = false
            setPending(false)
        }
    }
    return (
        <>
            <Button onClick={() => setOpen(true)}>
                {m.einvoice_publish()}
            </Button>
            <Dialog
                open={open}
                onOpenChange={(value) => {
                    if (!inFlight.current) setOpen(value)
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.einvoice_publish_confirm_title()}
                        </DialogTitle>
                        <DialogDescription>
                            {orderCode && <strong>{orderCode}: </strong>}
                            {m.einvoice_publish_confirm_hint()}
                        </DialogDescription>
                    </DialogHeader>
                    {requireManualCheck && orderId ? (
                        <EInvoiceRepublishCandidates
                            orderId={orderId}
                            onUseCandidate={onUseCandidate}
                        />
                    ) : null}
                    {requireManualCheck ? (
                        <div className="space-y-1">
                            <label className="flex items-start gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={checked}
                                    onChange={(event) => {
                                        setChecked(event.target.checked)
                                        setCheckError(false)
                                    }}
                                />
                                {m.einvoice_republish_check()}
                            </label>
                            {checkError ? (
                                <p
                                    role="alert"
                                    className="text-sm text-destructive"
                                >
                                    {m.einvoice_republish_check_required()}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            disabled={pending}
                            onClick={() => setOpen(false)}
                        >
                            {m.einvoice_publish_back()}
                        </Button>
                        <Button
                            disabled={pending}
                            onClick={() => {
                                void confirm()
                            }}
                        >
                            {m.einvoice_publish_confirm()}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
